import React, { memo, useEffect, useState } from 'react';
import CustomModal from './CustomModal';
import { Button, Grid, Radio, RadioGroup, FormControlLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LoadingSpinner from '../Loader/LoadingSpinner';
import style from '../../pages/css/patients.module.css';
import join from 'url-join';
import { ApiServerUrl } from '../../config.json';
import { useLang } from '../../util/lang';

const BASE_LANG_KEY = 'smileDesign.clinicalApprovement.upgradeTpOption';

const useStyles = makeStyles({
    radio: {
        '& [class*="PrivateRadioButtonIcon-root"]': {
            position: 'relative',
            '&:after': {
                background: '#fff',
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%',
                borderRadius: '50%',
                transform: 'scale(0.75)',
            },
        },
        '& .MuiSvgIcon-root': {
            zIndex: 1,
        },
    },
});

const UpdateCaseTypeModal = ({
    title,
    open,
    modalSize = 'md',
    handleClose,
    submitButton = true,
    handleDataSave,
    submitButtonLabel = 'Confirm',
    closeButtonLabel = 'Back',
    modalError = undefined,

    optionSelected,
    setOptionSelected,
    productMatrixArray = [],

    defaultCaseType = null,

    isFetchingProductmatrixForDr = false,
    isAllowEscapeTreatmentPlanApproval,
}) => {
    const classes = useStyles();

    const getSmileDesignUpgradeTpOptionLang = useLang('PatientInfo');

    const [approveMode, setApproveMode] = useState(null);

    title = getSmileDesignUpgradeTpOptionLang(`${BASE_LANG_KEY}.title`);

    useEffect(() => {
        if (open) {
            setApproveMode(null);
        }
    }, [open]);

    useEffect(() => {
        if (approveMode === null && open) {
            setOptionSelected('');
        }
    }, [approveMode, open]);

    const wrappedHandleDataSave = handleDataSave ? () => handleDataSave(approveMode) : handleDataSave;

    const isConfirmDisabled =
        approveMode === null ||
        (approveMode === 'upgrade' &&
            (!optionSelected || optionSelected === '' || optionSelected.case_type === defaultCaseType.case_type));

    const closeLabel = closeButtonLabel === 'Back' ? 'Cancel' : closeButtonLabel;

    const renderProductGrid = () => {
        if (isFetchingProductmatrixForDr && productMatrixArray.length === 0) {
            return <LoadingSpinner />;
        }

        return (
            <Grid container>
                {productMatrixArray?.map((v) => {
                    if (
                        /SMILE_DESIGN/.test(v.case_type) ||
                        v.case_type === 'RETAINER' ||
                        v.case_type === 'CHOOSE_LATER'
                    ) {
                        return null;
                    }

                    const isCurrent = v.case_type === defaultCaseType.case_type;
                    const isAbove = v.step_no > defaultCaseType.step_no;
                    const isBelow = v.step_no < defaultCaseType.step_no;

                    let isClickable = v.allowUpgrade;
                    let showNotApplicable = false;
                    let isDimmed = false;
                    let showCurrentRibbon = false;
                    let showUpgradeRibbon = false;
                    let showSelectedBorder = false;
                    let showUpgradeBorder = false;

                    if (!approveMode || approveMode === 'approve_current') {
                        // Nothing selected or "Approve current" → all unclickable
                        isClickable = false;
                        // Current option gets special treatment, others dimmed
                        isDimmed = !isCurrent;
                        // Below current = Not Applicable
                        showNotApplicable = isBelow;
                        // Current option: grey border + grey "current" ribbon
                        showSelectedBorder = isCurrent;
                        showCurrentRibbon = isCurrent;
                    } else if (approveMode === 'upgrade') {
                        // Only above + allowUpgrade are clickable
                        isClickable = isAbove && v.allowUpgrade;
                        // Current & below = Not Applicable
                        showNotApplicable = isCurrent || isBelow;
                        // Dim current & below
                        isDimmed = isCurrent || isBelow;
                        // If a specific upgrade option is selected, show green border + ribbon
                        if (
                            optionSelected &&
                            optionSelected !== '' &&
                            optionSelected.case_type === v.case_type &&
                            !isCurrent
                        ) {
                            showUpgradeBorder = true;
                            showUpgradeRibbon = true;
                        }
                    }

                    const cardClasses = [isDimmed ? style.optionDimmed : ''].filter(Boolean).join(' ');

                    const borderClass = showUpgradeBorder
                        ? style.optionUpgradeBorder
                        : showSelectedBorder
                          ? style.optionSelectedBorder
                          : '';

                    return (
                        <Grid
                            item
                            xs={6}
                            sm={3}
                            style={{ textAlign: 'center', padding: '4px 4px' }}
                            key={`TrioClear-${v.case_type}`}
                        >
                            <div
                                className={`${style.optionCardWrapper} ${showNotApplicable ? style.notApplicableCard : ''}`}
                            >
                                <div
                                    className={`${style.NewCaseModalLink} ${cardClasses}`}
                                    style={{
                                        color: 'unset',
                                        textDecoration: 'none',
                                    }}
                                    onClick={() => {
                                        if (isClickable) setOptionSelected(v);
                                    }}
                                >
                                    <div className={style.NewCaseModalBackground}></div>
                                    <Button
                                        className={`${style.NewCaseModalButton} ${borderClass}`}
                                        style={{
                                            backgroundColor: showNotApplicable ? '#282926' : v['color_code'],
                                            opacity: showNotApplicable ? 0.75 : 1,
                                        }}
                                    >
                                        <img
                                            className={style.NewCaseModalImage}
                                            src={join(ApiServerUrl, `/file/config/${v.product_image}`)}
                                        />
                                        <div className={style.NewCaseModalName}>{v['product_name'].toUpperCase()}</div>

                                        {!showNotApplicable && (
                                            <div
                                                className={
                                                    (v['product_desc'] == null || v['product_desc'].length === 0
                                                        ? `${style.NewCaseModalNoDescription}`
                                                        : `${style.NewCaseModalDescription}`) +
                                                    ` ${style.hoverTextCompact} ${borderClass}`
                                                }
                                            >
                                                <ul>
                                                    {v['product_desc']?.split(/\r?\n/).map((desc) => (
                                                        <li key={desc}>{desc}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {showCurrentRibbon && (
                                            <div className={`${style.cornerRibbon} ${style.cornerRibbonCurrent}`}>
                                                <span className={style.cornerRibbonText}>
                                                    {getSmileDesignUpgradeTpOptionLang(
                                                        `${BASE_LANG_KEY}.currentRibbon`
                                                    )}
                                                </span>
                                            </div>
                                        )}

                                        {showUpgradeRibbon && (
                                            <div className={`${style.cornerRibbon} ${style.cornerRibbonUpgrade}`}>
                                                <img
                                                    className={style.cornerRibbonCheckmark}
                                                    src="/img/checkmark.png"
                                                    alt="✓"
                                                />
                                                <span className={style.cornerRibbonText}>
                                                    {getSmileDesignUpgradeTpOptionLang(
                                                        `${BASE_LANG_KEY}.upgradeRibbon`
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                    </Button>
                                </div>
                                {showNotApplicable && (
                                    <div className={style.notApplicableOverlay}>
                                        {getSmileDesignUpgradeTpOptionLang(`${BASE_LANG_KEY}.notApplicable`)}
                                    </div>
                                )}
                            </div>
                        </Grid>
                    );
                })}
            </Grid>
        );
    };

    return (
        <CustomModal
            title={title}
            open={open}
            handleClose={handleClose}
            modalSize={modalSize}
            submitButton={submitButton}
            handleDataSave={wrappedHandleDataSave}
            submitButtonLabel={submitButtonLabel}
            closeButtonLabel={closeLabel}
            disabledSubmitButton={isConfirmDisabled}
            disabledCloseButton={!isAllowEscapeTreatmentPlanApproval}
            modalError={modalError}
        >
            <Grid container spacing={3} style={{ padding: '20px' }}>
                <Grid item xs={12} style={{ textAlign: 'start' }}>
                    <span style={{ fontWeight: 'bold',  verticalAlign: 'middle' }}>
                        {getSmileDesignUpgradeTpOptionLang(`${BASE_LANG_KEY}.currentTpOption`)}:{' '}
                    </span>
                    <span
                        style={{
                            color: '#39A8BD',
                            fontWeight: '900',
                            fontSize: '1.15em',
                            verticalAlign: 'middle',
                            fontFamily: '"Montserrat Black", "Montserrat", sans-serif',
                        }}
                    >
                        {defaultCaseType['product_name']}
                    </span>
                </Grid>

                <Grid item xs={12} style={{ textAlign: 'start' }}>
                    {getSmileDesignUpgradeTpOptionLang(`${BASE_LANG_KEY}.description`)}
                </Grid>

                <Grid item xs={12} style={{ textAlign: 'start' }}>
                    <RadioGroup
                        value={approveMode}
                        onChange={(e) => {
                            const val = e.target.value;
                            setApproveMode(val);
                            if (val === 'approve_current') {
                                setOptionSelected(defaultCaseType);
                            } else {
                                setOptionSelected('');
                            }
                        }}
                    >
                        <FormControlLabel
                            value="approve_current"
                            control={<Radio classes={{ root: classes.radio }} />}
                            label={
                                <span>
                                    <strong>
                                        {getSmileDesignUpgradeTpOptionLang(`${BASE_LANG_KEY}.approveCurrentRadioBold`)}
                                    </strong>{' '}
                                    {getSmileDesignUpgradeTpOptionLang(`${BASE_LANG_KEY}.approveCurrentRadioRest`)}
                                </span>
                            }
                        />
                        <FormControlLabel
                            value="upgrade"
                            control={<Radio classes={{ root: classes.radio }} />}
                            label={
                                <span>
                                    <strong>
                                        {getSmileDesignUpgradeTpOptionLang(`${BASE_LANG_KEY}.upgradeRadioBold`)}
                                    </strong>{' '}
                                    {getSmileDesignUpgradeTpOptionLang(`${BASE_LANG_KEY}.upgradeRadioRest`)}
                                </span>
                            }
                        />
                    </RadioGroup>
                </Grid>

                <Grid item xs={12} style={{ textAlign: 'start' }}>
                    {renderProductGrid()}
                </Grid>
            </Grid>
        </CustomModal>
    );
};

export default memo(UpdateCaseTypeModal);
